import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { contactInfo, brandInfo } from '../data/mock';
import { Mail, Phone, MapPin, Send, Music } from 'lucide-react';
import { toast } from 'sonner';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    toast.success('Message sent successfully!', {
      description: 'We\'ll get back to you soon.',
    });
    
    setFormData({ name: '', email: '', subject: '', message: '' });
    setIsSubmitting(false);
  };

  return (
    <section id="contact" className="py-24 lg:py-32 bg-grey">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left Column - Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="service-tag" style={{ backgroundColor: '#88a2ff', color: '#151515' }}>
              Get In Touch
            </span>
            <h2 className="font-display text-4xl md:text-6xl font-extrabold uppercase text-black mt-4 mb-6">
              Let's Create<br />
              <span className="text-dark-grey">Together</span>
            </h2>
            <p className="text-dark-grey text-lg leading-relaxed mb-12">
              Whether you're an artist looking for a home, a brand seeking collaboration, or just want to connect — we'd love to hear from you.
            </p>

            {/* Contact Info */}
            <div className="space-y-6 mb-12">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-black flex items-center justify-center">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-mono text-xs uppercase tracking-widest text-dark-grey mb-1">Email</p>
                  <a href={`mailto:${contactInfo.email}`} className="text-black hover:text-mid-purple transition-colors font-medium">
                    {contactInfo.email}
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-black flex items-center justify-center">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-mono text-xs uppercase tracking-widest text-dark-grey mb-1">Phone</p>
                  <a href={`tel:${contactInfo.phone}`} className="text-black hover:text-mid-purple transition-colors font-medium">
                    {contactInfo.phone}
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-black flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-mono text-xs uppercase tracking-widest text-dark-grey mb-1">Location</p>
                  <p className="text-black font-medium">{contactInfo.location}</p>
                </div>
              </div>
            </div>

            {/* Demo Submission CTA */}
            <div className="bg-black rounded-lg p-6">
              <div className="flex items-center gap-3 mb-3">
                <Music className="w-5 h-5 text-light-pink" />
                <h4 className="font-display text-lg font-bold text-white uppercase">Submit Your Demo</h4>
              </div>
              <p className="text-white/60 text-sm mb-4">
                Are you an artist looking to join our roster? Send us your best work.
              </p>
              <a 
                href="https://voice-audition-app.preview.emergentagent.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="cta-button w-full justify-center" 
                style={{ backgroundColor: '#ffd1e7', color: '#151515', borderColor: '#ffd1e7' }}
              >
                Submit Demo
              </a>
            </div>
          </motion.div>

          {/* Right Column - Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <form onSubmit={handleSubmit} className="bg-white rounded-lg p-8 shadow-lg">
              <h3 className="font-display text-2xl font-bold text-black uppercase mb-8">Send a Message</h3>
              
              <div className="space-y-6">
                <div>
                  <label htmlFor="name" className="block font-mono text-xs uppercase tracking-widest text-dark-grey mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-black transition-colors text-black"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block font-mono text-xs uppercase tracking-widest text-dark-grey mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-black transition-colors text-black"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block font-mono text-xs uppercase tracking-widest text-dark-grey mb-2">
                    Subject
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-black transition-colors text-black bg-white"
                  >
                    <option value="">Select a subject</option>
                    <option value="general">General Inquiry</option>
                    <option value="collaboration">Collaboration</option>
                    <option value="booking">Booking</option>
                    <option value="demo">Demo Submission</option>
                    <option value="press">Press & Media</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block font-mono text-xs uppercase tracking-widest text-dark-grey mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-black transition-colors text-black resize-none"
                    placeholder="Your message..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="cta-button w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    'Sending...'
                  ) : (
                    <>
                      Send Message
                      <Send className="w-4 h-4 ml-2" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
